import type { SVGProps } from "react";

import { cn } from "~/lib/utils";
import { type RootSessionStatus, rootSessionStatusTextClass } from "~/rootSessionStrip";
import "./SessionRing.css";

/** Session rings retain identity geometry and share Room status colors.
 * The center pip marks attention/new results; only working turns the arc.
 * Monitoring remains a steady outline and quiet retains a muted ring. */
const PIP_STATES: ReadonlySet<RootSessionStatus> = new Set(["approval", "input", "failed", "done"]);
const ARC_STATES: ReadonlySet<RootSessionStatus> = new Set(["working", "monitoring"]);

export function SessionRing({
  status,
  className,
  ...svgProps
}: Omit<SVGProps<SVGSVGElement>, "color"> & {
  readonly status: RootSessionStatus | null;
}) {
  const pip = status !== null && PIP_STATES.has(status);
  const arc = status !== null && ARC_STATES.has(status);
  return (
    <svg
      {...svgProps}
      aria-hidden="true"
      className={cn(
        "ncl-session-ring shrink-0",
        status === null ? "text-muted-foreground" : rootSessionStatusTextClass(status),
        className,
      )}
      data-session-ring=""
      data-session-status={status ?? "none"}
      fill="none"
      focusable="false"
      viewBox="0 0 24 24"
    >
      <circle
        className="ncl-session-ring-track"
        cx="12"
        cy="12"
        r="8.5"
        stroke="currentColor"
        strokeWidth="2.6"
      />
      {arc ? (
        <circle
          className="ncl-session-ring-arc"
          cx="12"
          cy="12"
          r="8.5"
          stroke="currentColor"
          strokeDasharray="36 17.4"
          strokeLinecap="round"
          strokeWidth="2.6"
        />
      ) : null}
      {pip ? (
        <circle className="ncl-session-ring-pip" cx="12" cy="12" fill="currentColor" r="3.2" />
      ) : null}
    </svg>
  );
}
